import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, debounceTime, merge, Subject, takeUntil } from 'rxjs';
import { LogService } from '../baseServices/logging/log.service';
import { RepositoryService } from '../baseServices/repository.service';
import { StoreName } from '../entities/StoreName';
import { WorkAssignment } from '../entities/WorkAssignment';
import { DateTimeHelper } from '../utils/date-time-helper';
import { AppUserClockStateService } from './app-user-clock-state.service';
import { AppUserService } from './app-user.service';
import { AssignmentService } from './assignment.service';

@Injectable({ providedIn: 'root' })
export class AppUserAssignmentService implements OnDestroy {
  private static readonly RECORD_ID = 1;

  private readonly currentAssignmentSubject = new BehaviorSubject<WorkAssignment | null>(null);
  readonly currentAssignment$ = this.currentAssignmentSubject.asObservable();
  get currentAssignment(): WorkAssignment | null { return this.currentAssignmentSubject.value; }

  private destroy$ = new Subject<void>();
  
  constructor(
    private logService: LogService,
    private repository: RepositoryService,
    private assignmentService: AssignmentService,
    private appUserService: AppUserService,
    private appUserClockStateService: AppUserClockStateService,
  ) {
    this.loadSubscriptions();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async changeAssignment(workAssignment: WorkAssignment) {
    if (this.appUserClockStateService?.clockState?.activeTimeRecord) {
      throw Error("Current assignment cannot be changed while the user is clocked in. It must match the user's active time record.")
    }

    const isAssignmentValid = await this.assignmentService.checkIsAssignmentValid(workAssignment, this.appUserService.currentUserId, DateTimeHelper.getLocalNow());
    if (!isAssignmentValid) {
      throw Error("New assignment is not valid.")
    }

    this.setCurrentWorkAssignment(workAssignment);
  }

  private loadSubscriptions() {
    merge(
      this.appUserService.currentUser$,
      this.appUserClockStateService.clockState$
    )
      .pipe(
        debounceTime(50),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.updateWorkAssignmentIfNeeded());
  }

  private async updateWorkAssignmentIfNeeded() {
    let workAssignment: WorkAssignment;
    
    if (this.appUserClockStateService.clockState?.activeTimeRecord) {
      workAssignment = await this.assignmentService.getWorkAssignmentFromTimeRecord(this.appUserClockStateService.clockState.activeTimeRecord);
    } else {
      workAssignment = await this.getWorkAssignmentFromStorage();
    }

    if (workAssignment) {
      const isAssignmentValid = await this.assignmentService.checkIsAssignmentValid(workAssignment, this.appUserService.currentUserId, DateTimeHelper.getLocalNow());
      if (isAssignmentValid) {
        this.setCurrentWorkAssignment(workAssignment);
        return;
      }
    }

    this.setNullWorkAssignment();
  }

  private async setCurrentWorkAssignment(workAssignment: WorkAssignment): Promise<void> {
    const noChange = await this.assignmentService.checkWorkAssignmentsMatch(this.appUserService.currentUserId, this.currentAssignment, workAssignment);
    if (noChange) return;
    
    const record = {
      ProjectId: workAssignment.ProjectId,
      TaskId: workAssignment.TaskId,
      LaborCategoryId: workAssignment.LaborCategoryId,
      FIPSStateId: workAssignment.FIPSStateId,
      FieldActivityId: workAssignment.FieldActivityId,
      RecordId: AppUserAssignmentService.RECORD_ID,
      LastModified: DateTimeHelper.getUtcNowString(),
    };
    await this.repository.create(record, StoreName.CurrentWorkAssignment);
    this.currentAssignmentSubject.next(workAssignment);
    this.logService.info('AppUserAssignmentService.SetCurrentWorkAssignment', { workAssignment });
  }

  private async setNullWorkAssignment(): Promise<void> {
    const prev = this.currentAssignment;
    if (prev !== null) {
      await this.repository.delete(AppUserAssignmentService.RECORD_ID, StoreName.CurrentWorkAssignment);
      this.currentAssignmentSubject.next(null);
      this.logService.info('AppUserAssignment.WorkAssignmentNulled', { previousAssignment: prev });
    }
  }

  private async getWorkAssignmentFromStorage(): Promise<WorkAssignment | null> {
    const record = await this.repository.getByLocalId(StoreName.CurrentWorkAssignment, AppUserAssignmentService.RECORD_ID);
    
    if (!record) {
      return null;
    }
    
    const workAssignment: WorkAssignment = {
      ProjectId: record.ProjectId,
      TaskId: record.TaskId,
      LaborCategoryId: record.LaborCategoryId,
      FIPSStateId: record.FIPSStateId,
      FieldActivityId: record.FieldActivityId,
    }

    return workAssignment;
  }
}
